import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

interface IconValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

export const usePWAIconValidation = () => {
  const [isValidating, setIsValidating] = useState(false);
  const { toast } = useToast();

  const validateIcon = async (file: File, targetSize: { width: number; height: number }): Promise<IconValidationResult> => {
    setIsValidating(true);
    const result: IconValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    try {
      // Check file type
      if (!file.type.startsWith('image/')) {
        result.errors.push('File must be an image');
        result.isValid = false;
      }

      // Check file size (max 2MB for PWA icons)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        result.errors.push('File size must be less than 2MB');
        result.isValid = false;
      }

      // Check image dimensions and aspect ratio
      const imageUrl = URL.createObjectURL(file);
      const img = new Image();
      
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = imageUrl;
      });

      // Clean up object URL
      URL.revokeObjectURL(imageUrl);

      // Check if image is square (PWA requirement)
      if (img.width !== img.height) {
        result.warnings.push('PWA icons should be square. The image will be resized to fit.');
      }

      // Check minimum resolution
      const minSize = Math.min(targetSize.width, targetSize.height);
      if (img.width < minSize || img.height < minSize) {
        result.warnings.push(`Image resolution is lower than recommended (${minSize}x${minSize}). This may result in blurry icons on some devices.`);
      }

      // Check if image is too large (will be resized anyway)
      if (img.width > 1024 || img.height > 1024) {
        result.warnings.push('Image will be resized to fit PWA requirements.');
      }

      // Additional PWA-specific checks
      if (targetSize.width >= 192) {
        // For larger icons, recommend higher quality
        if (file.size < 10 * 1024) { // Less than 10KB
          result.warnings.push('Small file size may result in lower quality icons. Consider using a higher quality image.');
        }
      }

      // Check for transparency support
      if (file.type === 'image/jpeg') {
        result.warnings.push('JPEG format does not support transparency. Consider using PNG for better PWA icon quality.');
      }

    } catch (error) {
      result.errors.push('Failed to validate image. Please try a different file.');
      result.isValid = false;
    } finally {
      setIsValidating(false);
    }

    // Show validation results
    if (result.errors.length > 0) {
      toast({
        title: "Icon validation failed",
        description: result.errors.join(', '),
        variant: "destructive",
      });
    } else if (result.warnings.length > 0) {
      toast({
        title: "Icon validation warnings",
        description: result.warnings.join(', '),
        variant: "default",
      });
    }

    return result;
  };

  const validatePWAIconSet = (icons: Record<string, string>): IconValidationResult => {
    const result: IconValidationResult = {
      isValid: true,
      errors: [],
      warnings: []
    };

    // Required PWA icon sizes
    const requiredSizes = ['192', '512'];
    const recommendedSizes = ['72', '96', '128', '144', '152', '192', '384', '512'];

    // Check for required sizes
    requiredSizes.forEach(size => {
      if (!icons[`icon${size}`]) {
        result.errors.push(`Required ${size}x${size} icon is missing`);
        result.isValid = false;
      }
    });

    // Check for recommended sizes
    recommendedSizes.forEach(size => {
      if (!icons[`icon${size}`]) {
        result.warnings.push(`Recommended ${size}x${size} icon is missing`);
      }
    });

    // Apple touch icon specific checks
    if (!icons.appleTouchIcon && !icons.icon180) {
      result.warnings.push('Apple touch icon (180x180) is recommended for iOS devices');
    }

    return result;
  };

  return {
    validateIcon,
    validatePWAIconSet,
    isValidating
  };
};